import { useState, useEffect } from 'react';
import Api from '../../../Api/api';
import { toast } from 'react-toastify';
import Modal from '../../../components/modal/modal';
import * as ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';

export default function AdminWeeklyReport() {
  const [rmaEntries, setRmaEntries] = useState([]);
  const [newEntry, setNewEntry] = useState({
    email: '',
    type: 'an'
  });
  const [editingId, setEditingId] = useState(null);
  const [editingEntry, setEditingEntry] = useState({});
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [previewHTML, setPreviewHTML] = useState('');
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [schedule, setSchedule] = useState({ dayOfWeek: 1, time: '08:00' });
  const [scheduleLoading, setScheduleLoading] = useState(false);
  const [savingSchedule, setSavingSchedule] = useState(false);
  const [downloadingExcel, setDownloadingExcel] = useState(false);

  // Filter states
  const [companies, setCompanies] = useState([]);
  const [vps, setVps] = useState([]);
  const [selectedCompanies, setSelectedCompanies] = useState([]);
  const [selectedVPs, setSelectedVPs] = useState([]);
  const [filtersLoading, setFiltersLoading] = useState(false);
  const [companySearchTerm, setCompanySearchTerm] = useState('');
  const [vpSearchTerm, setVpSearchTerm] = useState('');
  const [companyDropdownOpen, setCompanyDropdownOpen] = useState(false);
  const [vpDropdownOpen, setVpDropdownOpen] = useState(false);

  // Cache for FP data to avoid duplicate API calls
  const [cachedFPData, setCachedFPData] = useState(null);

  // Report cache state
  const [reportCache, setReportCache] = useState(new Map());
  const [lastGeneratedReport, setLastGeneratedReport] = useState(null);
  const [cacheUsed, setCacheUsed] = useState(false);
  const [showCacheModal, setShowCacheModal] = useState(false);

  // Always use DGG module in background
  const MODULE = 'DGG';

  // Cache utility functions
  const generateCacheKey = (companies, vps) => {
    const sortedCompanies = [...companies].sort().join(',');
    const sortedVPs = [...vps].sort().join(',');
    return `${sortedCompanies}||${sortedVPs}`;
  };

  const isCacheValid = (cacheEntry) => {
    const TTL = 10 * 60 * 1000; // 10 minutes
    return Date.now() - cacheEntry.timestamp < TTL;
  };

  const openCacheModal = () => {
    setShowCacheModal(true);
  };

  const invalidateCache = () => {
    setReportCache(new Map());
    setLastGeneratedReport(null);
    setShowCacheModal(false);
    toast.success('Cache erfolgreich geleert');
  };

  const invalidateSpecificCache = (cacheKey) => {
    setReportCache(prev => {
      const newCache = new Map(prev);
      newCache.delete(cacheKey);
      return newCache;
    });

    toast.success('Bericht aus Cache entfernt');
  };

  // Get cache entries as array for modal display
  const getCacheEntries = () => {
    const entries = [];
    reportCache.forEach((value, key) => {
      // Parse cache key to extract filter info
      const [companiesStr, vpsStr] = key.split('||');
      const companies = companiesStr ? companiesStr.split(',').filter(Boolean) : [];
      const vps = vpsStr ? vpsStr.split(',').filter(Boolean) : [];

      entries.push({
        key: key,
        companies: companies,
        vps: vps,
        timestamp: value.timestamp,
        age: Math.round((Date.now() - value.timestamp) / 60000), // Age in minutes
        hasFilters: companies.length > 0 || vps.length > 0
      });
    });

    // Sort by timestamp (newest first)
    return entries.sort((a, b) => b.timestamp - a.timestamp);
  };

  // Note: sessionStorage caching disabled due to quota issues with large breakdown data
  // Cache is maintained in-memory only during the current session

  useEffect(() => {
    loadRMAEntries();
    loadSchedule();
    loadFilterOptions();
  }, []);

  const loadFilterOptions = async () => {
    setFiltersLoading(true);
    try {
      const formData = new FormData();
      formData.append('portal', 'dgg');
      formData.append('userID', 1);
      formData.append('page', 1);
      formData.append('rows', 999999);
      formData.append('sortColumn', 7);
      formData.append('sortMethod', 'asc');

      // Filter structure matching the actual API format
      formData.append('filterID', JSON.stringify({
        a: null,
        b: null,
        c: null,
        d: null,
        e: null,
        f: null,
        g: null,
        h: 111,   // MFlaufProj ID
        i: 112,
        j: 113,
        k: 114,   // MFinaktProj ID
        l: null
      }));

      formData.append('filter', JSON.stringify({
        a: null,
        b: null,
        c: null,
        d: null,
        e: null,
        f: null,
        g: null,
        h: 1,     // MFlaufProj = 1 (active projects)
        i: 1,     // MFbeendProj = 1 (finished projects)
        j: 1,     // MFabgebProj = 1 (completed/closed projects - INCLUDES MS13!)
        k: 1,     // MFinaktProj = 1 (include inactive)
        l: 0
      }));

      formData.append('dateFilter', JSON.stringify({
        id: null,
        value: null
      }));

      const response = await Api().post('/getBestands', formData);

      if (response.data && response.data.bestands) {
        // Cache the FP data for later use in report generation
        setCachedFPData(response.data.bestands);

        // Extract unique companies
        const uniqueCompanies = [...new Set(response.data.bestands.map(fp => fp.FirmaKurz).filter(Boolean))].sort();
        setCompanies(uniqueCompanies);

        // Extract unique VPs
        const uniqueVPs = [...new Set(response.data.bestands.map(fp => fp.ZustVP).filter(Boolean))].sort();
        setVps(uniqueVPs);
      }
    } catch (error) {
      toast.error('Fehler beim Laden der Filteroptionen');
      console.error(error);
    } finally {
      setFiltersLoading(false);
    }
  };

  const loadRMAEntries = async () => {
    try {
      const response = await Api().get('/getdataAdminRMAs');
      setRmaEntries(response.data);
    } catch (error) {
      toast.error('Fehler beim Laden der E-Mail-Empfänger');
      console.error(error);
    }
  };

  const addEntry = async () => {
    if (!newEntry.email) {
      toast.error('Bitte geben Sie eine E-Mail-Adresse ein');
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(newEntry.email)) {
      toast.error('Bitte geben Sie eine gültige E-Mail-Adresse ein');
      return;
    }

    try {
      // Use ID 0 for new entries - the stored procedure will auto-generate
      await Api().post('/setRMA', {
        rma_id: 0,
        rma_type: newEntry.type,
        rma_module: MODULE,
        rma_email: newEntry.email
      });
      toast.success('Empfänger erfolgreich hinzugefügt');
      setNewEntry({ email: '', type: 'an' });
      loadRMAEntries();
    } catch (error) {
      toast.error('Fehler beim Hinzufügen des Empfängers');
      console.error(error);
    }
  };

  const startEditing = (entry) => {
    setEditingId(entry.ID);
    setEditingEntry({ ...entry });
  };

  const cancelEditing = () => {
    setEditingId(null);
    setEditingEntry({});
  };

  const saveEdit = async (id) => {
    if (!editingEntry.Email) {
      toast.error('E-Mail-Adresse darf nicht leer sein');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(editingEntry.Email)) {
      toast.error('Bitte geben Sie eine gültige E-Mail-Adresse ein');
      return;
    }

    try {
      await Api().post('/setRMA', {
        rma_id: id,
        rma_type: editingEntry.Type,
        rma_module: MODULE,
        rma_email: editingEntry.Email
      });
      toast.success('Empfänger erfolgreich aktualisiert');
      setEditingId(null);
      setEditingEntry({});
      loadRMAEntries();
    } catch (error) {
      toast.error('Fehler beim Aktualisieren des Empfängers');
      console.error(error);
    }
  };

  const deleteEntry = async (id) => {
    if (!window.confirm('Möchten Sie diesen Empfänger wirklich löschen?')) return;

    try {
      await Api().delete(`/deleteRMA/${id}`);
      toast.success('Empfänger erfolgreich gelöscht');
      loadRMAEntries();
    } catch (error) {
      toast.error('Fehler beim Löschen des Empfängers');
      console.error(error);
    }
  };

  const addCompany = (company) => {
    if (company && !selectedCompanies.includes(company)) {
      setSelectedCompanies([...selectedCompanies, company]);
    }
  };

  const removeCompany = (company) => {
    setSelectedCompanies(selectedCompanies.filter(c => c !== company));
  };

  const addVP = (vp) => {
    if (vp && !selectedVPs.includes(vp)) {
      setSelectedVPs([...selectedVPs, vp]);
    }
  };

  const removeVP = (vp) => {
    setSelectedVPs(selectedVPs.filter(v => v !== vp));
  };

  const generatePreview = async () => {
    setLoading(true);
    setCacheUsed(false);

    try {
      const cacheKey = generateCacheKey(selectedCompanies, selectedVPs);

      // Check cache first
      const cachedReport = reportCache.get(cacheKey);
      if (cachedReport && isCacheValid(cachedReport)) {
        setPreviewHTML(cachedReport.html);
        setLastGeneratedReport({
          html: cachedReport.html,
          filters: { companies: selectedCompanies, vps: selectedVPs }
        });
        setIsPreviewOpen(true);
        setCacheUsed(true);
        toast.info('Bericht aus Cache geladen (gespeichert vor ' +
          Math.round((Date.now() - cachedReport.timestamp) / 60000) + ' Minuten)',
          { autoClose: 3000 }
        );
        setLoading(false);
        return;
      }

      // Generate new report
      const response = await Api().post('/sendWeeklyReport', {
        preview_only: true,
        user_id: 1,
        module: MODULE,
        filter_companies: selectedCompanies,
        filter_vps: selectedVPs,
        cached_fp_data: cachedFPData // Send cached data to avoid duplicate DB call
      });

      const reportHTML = response.data.html;
      setPreviewHTML(reportHTML);

      // Store in cache
      setReportCache(prev => {
        const newCache = new Map(prev);
        newCache.set(cacheKey, {
          html: reportHTML,
          timestamp: Date.now()
        });
        return newCache;
      });

      // Store as last generated for Preview→Send reuse
      setLastGeneratedReport({
        html: reportHTML,
        filters: { companies: selectedCompanies, vps: selectedVPs }
      });

      setIsPreviewOpen(true);
    } catch (error) {
      toast.error('Fehler beim Generieren der Vorschau');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const generateBreakdownExcel = async (data) => {
    const workbook = new ExcelJS.Workbook();
    workbook.creator = 'ProjektPortal';
    workbook.created = new Date();

    // Helper function to add section with breakdown
    const addBreakdownSheet = (sheetName, breakdownData, sectionType) => {
      const sheet = workbook.addWorksheet(sheetName);

      // Header styling (no background color)
      const headerStyle = {
        font: { bold: true },
        alignment: { vertical: 'middle', horizontal: 'left' }
      };

      let rowIndex = 1;

      if (sectionType === 'fp_overview') {
        // FP Overview breakdown - unified table with extended columns
        const allCompanies = [];

        // Collect all companies from all sections
        ['aktiv', 'abgeschlossen', 'abgesagt'].forEach(status => {
          ['DGG', 'HSG'].forEach(portal => {
            const included = breakdownData[status]?.[portal]?.included || [];
            const excluded = breakdownData[status]?.[portal]?.excluded || [];

            included.forEach(company => {
              allCompanies.push({
                portal: portal,
                status: status,
                fpId: company.FP_ID || '',
                firma: company.FirmaKurz,
                ma: company.MA,
                vp: company.ZustVP,
                pStatus: company.PStatus,
                included: true,
                grund: ''
              });
            });

            excluded.forEach(company => {
              allCompanies.push({
                portal: portal,
                status: status,
                fpId: company.FP_ID || '',
                firma: company.FirmaKurz,
                ma: company.MA,
                vp: company.ZustVP,
                pStatus: company.PStatus,
                included: false,
                grund: company.skip_reason || ''
              });
            });
          });
        });

        // Add header row
        sheet.getRow(rowIndex).values = ['DGG/HSG', 'aktiv/abgeschlossen', 'FP id', 'Firma', 'MA', 'VP', 'Status'];
        sheet.getRow(rowIndex).font = { bold: true };
        rowIndex++;

        // Add all companies
        allCompanies.forEach(company => {
          const row = sheet.getRow(rowIndex);
          row.values = [
            company.portal,
            company.status,
            company.fpId,
            company.firma,
            company.ma,
            company.vp,
            company.pStatus
          ];
          if (!company.included && company.grund) {
            // Add grund (reason) in the next column only for excluded items
            row.getCell(8).value = company.grund;
          }
          rowIndex++;
        });

        // Set column widths - Firma column 2x larger
        sheet.getColumn(1).width = 12; // DGG/HSG
        sheet.getColumn(2).width = 18; // aktiv/abgeschlossen
        sheet.getColumn(3).width = 10; // FP id
        sheet.getColumn(4).width = 40; // Firma (2x larger)
        sheet.getColumn(5).width = 10; // MA
        sheet.getColumn(6).width = 20; // VP
        sheet.getColumn(7).width = 15; // Status
        sheet.getColumn(8).width = 30; // Grund

      } else if (sectionType === 'potential') {
        // Potential breakdown - unified table with each service as a row
        const allServiceRows = [];

        ['DGG', 'HSG'].forEach(portal => {
          const included = breakdownData[portal]?.included || [];
          const excluded = breakdownData[portal]?.excluded || [];

          // Process included companies - each service becomes a row
          included.forEach(company => {
            Object.entries(company.services || {}).forEach(([serviceName, serviceData]) => {
              const unit = serviceName === 'bAV' ? 'Mio €' : '€';
              
              // IMPORTANT: Use raw_potential (undivided value) for Excel export
              // The division by 1000/1000000 is ONLY for email/dashboard display
              // Excel should show actual calculated values: MA × Beteiligung% × BWS
              const actualPotential = serviceData.raw_potential !== undefined ? serviceData.raw_potential : serviceData.potential;
              
              const calculation = serviceData.included
                ? `${company.MA} MA × ${serviceData.beteiligung.toFixed(1)}% × ${serviceData.bws}`
                : '-';
              
              // Build service status display: show actual status + inclusion status
              const actualStatus = serviceData.status || '--';
              const inclusionStatus = serviceData.included ? '✓ Inkludiert' : `✗ ${serviceData.reason}`;
              const combinedStatus = `${actualStatus} (${inclusionStatus})`;

              allServiceRows.push({
                portal: portal,
                fpName: company.FirmaKurz,
                fpId: company.FP_ID || '',
                ma: company.MA,
                vp: company.ZustVP,
                pStatus: company.PStatus,
                service: serviceName,
                serviceStatus: combinedStatus,
                bws: serviceData.bws,
                beteiligung: `${serviceData.beteiligung.toFixed(1)}%`,
                berechnung: calculation,
                potenzial: `${actualPotential.toFixed(2)} €`, // Always show in € with full precision
                included: true,
                grund: ''
              });
            });
          });

          // Process excluded companies - create one row per company with reason
          excluded.forEach(company => {
            allServiceRows.push({
              portal: portal,
              fpName: company.FirmaKurz,
              fpId: company.FP_ID || '',
              ma: company.MA,
              vp: company.ZustVP,
              pStatus: company.PStatus,
              service: '-',
              serviceStatus: '✗ Ausgeschlossen',
              bws: '-',
              beteiligung: '-',
              berechnung: '-',
              potenzial: '-',
              included: false,
              grund: company.skip_reason || ''
            });
          });
        });

        // Add header row
        sheet.getRow(rowIndex).values = [
          'DGG/HSG',
          'FP Name',
          'FP ID',
          'MA',
          'VP',
          'Status',
          'Service',
          'Service Status',
          'BWS',
          'Beteiligung%',
          'Berechnung',
          'Potenzial'
        ];
        sheet.getRow(rowIndex).font = { bold: true };
        rowIndex++;

        // Add all service rows
        allServiceRows.forEach(row => {
          const excelRow = sheet.getRow(rowIndex);
          excelRow.values = [
            row.portal,
            row.fpName,
            row.fpId,
            row.ma,
            row.vp,
            row.pStatus,
            row.service,
            row.serviceStatus,
            row.bws,
            row.beteiligung,
            row.berechnung,
            row.potenzial
          ];
          if (!row.included && row.grund) {
            // Add grund (reason) in the next column only for excluded items
            excelRow.getCell(13).value = row.grund;
          }
          rowIndex++;
        });

        // Set column widths for potential sheets
        sheet.getColumn(1).width = 12;  // DGG/HSG
        sheet.getColumn(2).width = 40;  // FP Name (2x larger like Firma)
        sheet.getColumn(3).width = 10;  // FP ID
        sheet.getColumn(4).width = 10;  // MA
        sheet.getColumn(5).width = 20;  // VP
        sheet.getColumn(6).width = 25;  // Status
        sheet.getColumn(7).width = 12;  // Service
        sheet.getColumn(8).width = 25;  // Service Status
        sheet.getColumn(9).width = 12;  // BWS
        sheet.getColumn(10).width = 15; // Beteiligung%
        sheet.getColumn(11).width = 40; // Berechnung
        sheet.getColumn(12).width = 15; // Potenzial
        sheet.getColumn(13).width = 30; // Grund
      }
    };

    // Add sheets for each breakdown section
    if (data.fp_overview_breakdown) {
      addBreakdownSheet('FP Overview', data.fp_overview_breakdown, 'fp_overview');
    }

    if (data.potential_pre_dlv_breakdown) {
      addBreakdownSheet('Potenzial vor DLV (MS 2-4)', data.potential_pre_dlv_breakdown, 'potential');
    }

    if (data.potential_post_dlv_breakdown) {
      addBreakdownSheet('Potenzial ab DLV (MS 5-12)', data.potential_post_dlv_breakdown, 'potential');
    }

    // Generate and download file
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    const fileName = `Wochenbericht_Breakdown_${new Date().toISOString().split('T')[0]}.xlsx`;
    saveAs(blob, fileName);
  };

  const downloadBreakdownExcel = async () => {
    setDownloadingExcel(true);
    try {
      toast.info('Excel-Datei wird generiert...', { autoClose: 2000 });

      // Fetch breakdown data from backend
      const response = await Api().post('/sendWeeklyReport', {
        preview_only: true,
        user_id: 1,
        module: MODULE,
        filter_companies: selectedCompanies,
        filter_vps: selectedVPs,
        cached_fp_data: cachedFPData
      });

      const breakdownData = response.data.data;

      // Generate Excel file using ExcelJS
      await generateBreakdownExcel(breakdownData);

      toast.success('Excel-Datei erfolgreich heruntergeladen');
    } catch (error) {
      toast.error('Fehler beim Herunterladen der Excel-Datei');
      console.error(error);
    } finally {
      setDownloadingExcel(false);
    }
  };

  const triggerManualSend = async () => {
    if (!window.confirm('Wochenbericht jetzt an alle Empfänger senden?')) return;

    setSending(true);
    setCacheUsed(false);

    try {
      // Check if we can reuse the last generated preview
      const canReusePreview = lastGeneratedReport &&
        JSON.stringify(lastGeneratedReport.filters.companies.sort()) === JSON.stringify([...selectedCompanies].sort()) &&
        JSON.stringify(lastGeneratedReport.filters.vps.sort()) === JSON.stringify([...selectedVPs].sort());

      let reportHTML;
      if (canReusePreview) {
        reportHTML = lastGeneratedReport.html;
        setCacheUsed(true);
        toast.info('Sende zuletzt generierte Vorschau (kein erneutes Berechnen nötig)', { autoClose: 2000 });
      } else {
        // Need to generate report if filters changed
        const cacheKey = generateCacheKey(selectedCompanies, selectedVPs);
        const cachedReport = reportCache.get(cacheKey);

        if (cachedReport && isCacheValid(cachedReport)) {
          reportHTML = cachedReport.html;
          setCacheUsed(true);
          toast.info('Sende Bericht aus Cache (gespeichert vor ' +
            Math.round((Date.now() - cachedReport.timestamp) / 60000) + ' Minuten)',
            { autoClose: 2000 }
          );
        } else {
          // Generate fresh report
          const previewResponse = await Api().post('/sendWeeklyReport', {
            preview_only: true,
            user_id: 1,
            module: MODULE,
            filter_companies: selectedCompanies,
            filter_vps: selectedVPs,
            cached_fp_data: cachedFPData
          });
          reportHTML = previewResponse.data.html;

          // Cache the newly generated report
          setReportCache(prev => {
            const newCache = new Map(prev);
            newCache.set(cacheKey, {
              html: reportHTML,
              timestamp: Date.now()
            });
            return newCache;
          });
        }
      }

      // Send the email with the report HTML
      const response = await Api().post('/sendWeeklyReport', {
        user_id: 1,
        module: MODULE,
        filter_companies: selectedCompanies,
        filter_vps: selectedVPs,
        cached_fp_data: cachedFPData,
        pregenerated_html: reportHTML // Send pre-generated HTML to skip backend recalculation
      });

      if (response.data.success) {
        toast.success(
          `Bericht an ${response.data.recipients} Empfänger gesendet (TO: ${response.data.breakdown.to}, CC: ${response.data.breakdown.cc}, BCC: ${response.data.breakdown.bcc})`
        );
      } else {
        toast.error('Fehler beim Senden des Berichts');
      }
    } catch (error) {
      toast.error('Fehler beim Senden des Berichts');
      console.error(error);
    } finally {
      setSending(false);
    }
  };

  const loadSchedule = async () => {
    setScheduleLoading(true);
    try {
      const response = await Api().get('/getWeeklyReportSchedule');
      if (response.data && response.data.DayOfWeek && response.data.Time) {
        setSchedule({
          dayOfWeek: response.data.DayOfWeek,
          time: response.data.Time
        });
      }
    } catch (error) {
      console.error('Failed to load schedule:', error);
    } finally {
      setScheduleLoading(false);
    }
  };

  const saveSchedule = async () => {
    setSavingSchedule(true);
    try {
      await Api().post('/updateWeeklyReportSchedule', {
        dayOfWeek: schedule.dayOfWeek,
        time: schedule.time
      });
      toast.success('Zeitplan erfolgreich aktualisiert');
    } catch (error) {
      toast.error('Fehler beim Aktualisieren des Zeitplans');
      console.error(error);
    } finally {
      setSavingSchedule(false);
    }
  };

  const getDayName = (dayNumber) => {
    const days = ['Sonntag', 'Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag'];
    return days[dayNumber] || 'Montag';
  };

  const getTypeLabel = (type) => {
    const labels = { 'an': 'An', 'cc': 'CC', 'bcc': 'BCC' };
    return labels[type] || type;
  };

  // Filter entries for DGG module only
  const filteredEntries = rmaEntries.filter(entry => entry.Module === MODULE);

  return (
    <div className="dashboardContainer">
      <h1 className="text-2xl font-bold mb-6 text-text text-left">Wochenbericht E-Mail-Empfänger</h1>

      <div className="shadow-lg rounded-lg bg-white my-4 border border-borderBlue" style={{width: '75%', marginLeft: 'auto', marginRight: 'auto'}}>
        {/* Schedule Configuration */}
        <section className="p-6 border-b border-borderBlue">
          <h2 className="text-xl font-semibold mb-4 text-text text-left">E-Mail Zeitplan</h2>

        {scheduleLoading ? (
          <div className="text-center py-4 text-grey">Zeitplan wird geladen...</div>
        ) : (
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-text mb-2">
                  Wochentag
                </label>
                <select
                  value={schedule.dayOfWeek}
                  onChange={(e) => setSchedule({ ...schedule, dayOfWeek: parseInt(e.target.value) })}
                  className="w-full border border-borderBlue rounded px-3 py-2 text-text focus:border-mainBlue focus:outline-none"
                >
                  <option value={0}>Sonntag</option>
                  <option value={1}>Montag</option>
                  <option value={2}>Dienstag</option>
                  <option value={3}>Mittwoch</option>
                  <option value={4}>Donnerstag</option>
                  <option value={5}>Freitag</option>
                  <option value={6}>Samstag</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-text mb-2">
                  Uhrzeit (24-Stunden-Format)
                </label>
                <input
                  type="time"
                  value={schedule.time}
                  onChange={(e) => setSchedule({ ...schedule, time: e.target.value })}
                  className="w-full border border-borderBlue rounded px-3 py-2 text-text focus:border-mainBlue focus:outline-none"
                />
              </div>
            </div>

            <div className="flex items-center gap-4">
              <button
                onClick={saveSchedule}
                disabled={savingSchedule}
                className="inline-flex items-center px-6 py-3 border border-transparent shadow-md text-sm font-semibold rounded-lg text-white bg-mainBlue hover:bg-opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-mainBlue transition-all disabled:bg-disableBlue disabled:cursor-not-allowed"
              >
                {savingSchedule ? (
                  <span className="flex items-center gap-2">
                    <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Wird gespeichert...
                  </span>
                ) : (
                  'Zeitplan speichern'
                )}
              </button>

              <div className="text-sm text-grey">
                Aktueller Zeitplan: <strong className="text-text">Jeden {getDayName(schedule.dayOfWeek)} um {schedule.time}</strong> (Europa/Berlin Zeitzone)
              </div>
            </div>

            <div className="p-4 bg-yellowLight border border-pending rounded">
              <p className="text-sm text-orangeDark">
                <strong>Hinweis:</strong> Die automatisierte E-Mail wird gemäß diesem Zeitplan gesendet.
                Stellen Sie sicher, dass der Laravel Scheduler (Cron-Job) auf dem Server läuft.
              </p>
            </div>
          </div>
        )}
      </section>

      {/* Recipients Management */}
      <section className="p-6 border-b border-borderBlue">
        <h2 className="text-xl font-semibold mb-4 text-text text-left">E-Mail-Empfänger</h2>

        {/* Add New Entry Form */}
        <div className="mb-4 grid grid-cols-1 md:grid-cols-3 gap-4">
          <input
            type="email"
            placeholder="E-Mail-Adresse"
            value={newEntry.email}
            onChange={(e) => setNewEntry({ ...newEntry, email: e.target.value })}
            onKeyPress={(e) => e.key === 'Enter' && addEntry()}
            className="border border-borderBlue rounded px-3 py-2 text-text focus:border-mainBlue focus:outline-none"
          />
          <select
            value={newEntry.type}
            onChange={(e) => setNewEntry({ ...newEntry, type: e.target.value })}
            className="border border-borderBlue rounded px-3 py-2 text-text focus:border-mainBlue focus:outline-none"
          >
            <option value="an">An (TO)</option>
            <option value="cc">CC</option>
            <option value="bcc">BCC</option>
          </select>
          <button
            onClick={addEntry}
            className="inline-flex items-center justify-center px-6 py-3 border border-transparent shadow-md text-sm font-semibold rounded-lg text-white bg-mainBlue hover:bg-opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-mainBlue transition-all"
          >
            Empfänger hinzufügen
          </button>
        </div>

        {filteredEntries.length === 0 ? (
          <div className="text-center py-8 text-grey">
            Keine Empfänger konfiguriert. Fügen Sie mindestens einen Empfänger hinzu.
          </div>
        ) : (
          <table className="w-full border-collapse border border-borderBlue">
            <thead>
              <tr className="bg-offWhite">
                <th className="border border-borderBlue p-2 text-left text-text">ID</th>
                <th className="border border-borderBlue p-2 text-left text-text">E-Mail</th>
                <th className="border border-borderBlue p-2 text-left text-text">Typ</th>
                <th className="border border-borderBlue p-2 text-left text-text">Aktionen</th>
              </tr>
            </thead>
            <tbody>
              {filteredEntries.map((entry) => (
                <tr key={entry.ID} className="hover:bg-lightgrey">
                  <td className="border border-borderBlue p-2 text-text">{entry.ID}</td>
                  <td className="border border-borderBlue p-2 text-text">
                    {editingId === entry.ID ? (
                      <input
                        type="email"
                        value={editingEntry.Email}
                        onChange={(e) => setEditingEntry({ ...editingEntry, Email: e.target.value })}
                        className="w-full border border-borderBlue rounded px-2 py-1 text-text focus:border-mainBlue focus:outline-none"
                      />
                    ) : (
                      entry.Email
                    )}
                  </td>
                  <td className="border border-borderBlue p-2 text-text">
                    {editingId === entry.ID ? (
                      <select
                        value={editingEntry.Type}
                        onChange={(e) => setEditingEntry({ ...editingEntry, Type: e.target.value })}
                        className="w-full border border-borderBlue rounded px-2 py-1 text-text focus:border-mainBlue focus:outline-none"
                      >
                        <option value="an">An (TO)</option>
                        <option value="cc">CC</option>
                        <option value="bcc">BCC</option>
                      </select>
                    ) : (
                      <span className={`px-2 py-1 rounded text-xs font-semibold ${
                        entry.Type === 'an' ? 'bg-complete text-green' :
                        entry.Type === 'cc' ? 'bg-yellowLight text-orangeDark' :
                        'bg-silver text-text'
                      }`}>
                        {getTypeLabel(entry.Type)}
                      </span>
                    )}
                  </td>
                  <td className="border border-borderBlue p-2">
                    {editingId === entry.ID ? (
                      <div className="flex gap-2">
                        <button
                          onClick={() => saveEdit(entry.ID)}
                          className="inline-flex items-center px-3 py-1.5 border border-transparent shadow-sm text-xs font-semibold rounded text-white bg-green hover:bg-opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green transition-all"
                        >
                          Speichern
                        </button>
                        <button
                          onClick={cancelEditing}
                          className="inline-flex items-center px-3 py-1.5 border border-transparent shadow-sm text-xs font-semibold rounded text-white bg-grey hover:bg-text focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-grey transition-all"
                        >
                          Abbrechen
                        </button>
                      </div>
                    ) : (
                      <div className="flex gap-2">
                        <button
                          onClick={() => startEditing(entry)}
                          className="inline-flex items-center px-3 py-1.5 border border-transparent shadow-sm text-xs font-semibold rounded text-white bg-mainBlue hover:bg-opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-mainBlue transition-all"
                        >
                          Bearbeiten
                        </button>
                        <button
                          onClick={() => deleteEntry(entry.ID)}
                          className="inline-flex items-center px-3 py-1.5 border border-transparent shadow-sm text-xs font-semibold rounded text-white bg-cancel hover:bg-red focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-cancel transition-all"
                        >
                          Löschen
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      {/* Filters */}
      <section className="p-6 border-b border-borderBlue">
        <h2 className="text-xl font-semibold mb-4 text-text text-left">Berichtsfilter</h2>

        {filtersLoading ? (
          <div className="text-center py-4 text-grey">Filter werden geladen...</div>
        ) : (
          <div className="space-y-4">
            {/* Company and VP Filters in One Row */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Company Filter */}
              <div className="min-w-0">
                <label className="block text-sm font-medium text-text mb-2">
                  Firmenprojekte (FP) filtern
                </label>
                <div className="relative mb-2">
                  <div className="relative">
                    <input
                      type="text"
                      placeholder="Firma suchen und auswählen..."
                      value={companySearchTerm}
                      onChange={(e) => setCompanySearchTerm(e.target.value)}
                      onFocus={() => setCompanyDropdownOpen(true)}
                      onBlur={() => setTimeout(() => setCompanyDropdownOpen(false), 200)}
                      className="w-full border border-borderBlue rounded px-3 py-2 pr-8 text-text focus:border-mainBlue focus:outline-none"
                    />
                    <button
                      onClick={() => setCompanyDropdownOpen(!companyDropdownOpen)}
                      className="absolute right-2 top-1/2 transform -translate-y-1/2 text-grey"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                  </div>
                  {companyDropdownOpen && (
                    <div className="absolute z-10 w-full mt-1 bg-white border border-borderBlue rounded shadow-lg max-h-60 overflow-y-auto">
                      {companies
                        .filter(c => !selectedCompanies.includes(c) && (companySearchTerm === '' || c.toLowerCase().includes(companySearchTerm.toLowerCase())))
                        .map(company => (
                          <div
                            key={company}
                            onClick={() => {
                              addCompany(company);
                              setCompanySearchTerm('');
                              setCompanyDropdownOpen(false);
                            }}
                            className="px-3 py-2 hover:bg-lightgrey cursor-pointer text-text text-left"
                          >
                            {company}
                          </div>
                        ))}
                      {companies.filter(c => !selectedCompanies.includes(c) && (companySearchTerm === '' || c.toLowerCase().includes(companySearchTerm.toLowerCase()))).length === 0 && (
                        <div className="px-3 py-2 text-grey text-sm text-left">Keine Ergebnisse</div>
                      )}
                    </div>
                  )}
                </div>
                {selectedCompanies.length > 0 && (
                  <div className="flex flex-wrap gap-2 p-2 border border-borderBlue rounded bg-lightgrey">
                    {selectedCompanies.map(company => (
                      <span
                        key={company}
                        className="inline-flex items-center gap-1 px-3 py-1 bg-offWhite border border-borderBlue text-text rounded-full text-sm"
                      >
                        {company}
                        <button
                          onClick={() => removeCompany(company)}
                          className="hover:bg-silver rounded-full"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </span>
                    ))}
                  </div>
                )}
                {selectedCompanies.length === 0 && (
                  <p className="text-xs text-grey mt-1">Keine Filter aktiv - alle FPs werden berücksichtigt</p>
                )}
              </div>

              {/* VP Filter */}
              <div className="min-w-0">
                <label className="block text-sm font-medium text-text mb-2">
                  Vertriebspartner (VP) filtern
                </label>
                <div className="relative mb-2">
                  <div className="relative">
                    <input
                      type="text"
                      placeholder="VP suchen und auswählen..."
                      value={vpSearchTerm}
                      onChange={(e) => setVpSearchTerm(e.target.value)}
                      onFocus={() => setVpDropdownOpen(true)}
                      onBlur={() => setTimeout(() => setVpDropdownOpen(false), 200)}
                      className="w-full border border-borderBlue rounded px-3 py-2 pr-8 text-text focus:border-mainBlue focus:outline-none"
                    />
                    <button
                      onClick={() => setVpDropdownOpen(!vpDropdownOpen)}
                      className="absolute right-2 top-1/2 transform -translate-y-1/2 text-grey"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                  </div>
                  {vpDropdownOpen && (
                    <div className="absolute z-10 w-full mt-1 bg-white border border-borderBlue rounded shadow-lg max-h-60 overflow-y-auto">
                      {vps
                        .filter(vp => !selectedVPs.includes(vp) && (vpSearchTerm === '' || vp.toLowerCase().includes(vpSearchTerm.toLowerCase())))
                        .map(vp => (
                          <div
                            key={vp}
                            onClick={() => {
                              addVP(vp);
                              setVpSearchTerm('');
                              setVpDropdownOpen(false);
                            }}
                            className="px-3 py-2 hover:bg-lightgrey cursor-pointer text-text text-left"
                          >
                            {vp}
                          </div>
                        ))}
                      {vps.filter(vp => !selectedVPs.includes(vp) && (vpSearchTerm === '' || vp.toLowerCase().includes(vpSearchTerm.toLowerCase()))).length === 0 && (
                        <div className="px-3 py-2 text-grey text-sm text-left">Keine Ergebnisse</div>
                      )}
                    </div>
                  )}
                </div>
                {selectedVPs.length > 0 && (
                  <div className="flex flex-wrap gap-2 p-2 border border-borderBlue rounded bg-lightgrey">
                    {selectedVPs.map(vp => (
                      <span
                        key={vp}
                        className="inline-flex items-center gap-1 px-3 py-1 bg-offWhite border border-borderBlue text-text rounded-full text-sm"
                      >
                        {vp}
                        <button
                          onClick={() => removeVP(vp)}
                          className="hover:bg-silver rounded-full"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </span>
                    ))}
                  </div>
                )}
                {selectedVPs.length === 0 && (
                  <p className="text-xs text-grey mt-1">Keine Filter aktiv - alle VPs werden berücksichtigt</p>
                )}
              </div>
            </div>

            <div className="p-4 bg-yellowLight border border-pending rounded">
              <p className="text-sm text-orangeDark">
                <strong>Hinweis:</strong> Die Filter werden bei der Vorschau und beim Senden angewendet.
                Wenn keine Filter ausgewählt sind, werden alle Firmenprojekte im Bericht berücksichtigt.
              </p>
            </div>
          </div>
        )}
      </section>

      {/* Actions */}
      <section className="p-6">
        <h2 className="text-xl font-semibold mb-4 text-text text-left">Aktionen</h2>

        <div className="flex gap-4 mb-4">
          <button
            onClick={generatePreview}
            disabled={loading}
            className="inline-flex items-center px-6 py-3 border border-transparent shadow-md text-sm font-semibold rounded-lg text-white bg-grey hover:bg-text focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-grey transition-all disabled:bg-disableBlue disabled:cursor-not-allowed"
          >
            {loading ? (
              <span className="flex items-center gap-2">
                <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Wird generiert...
              </span>
            ) : (
              'Bericht Vorschau'
            )}
          </button>

          <button
            onClick={triggerManualSend}
            disabled={sending}
            className="inline-flex items-center px-6 py-3 border border-transparent shadow-md text-sm font-semibold rounded-lg text-white bg-green hover:bg-opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green transition-all disabled:bg-disableBlue disabled:cursor-not-allowed"
          >
            {sending ? (
              <span className="flex items-center gap-2">
                <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Wird gesendet...
              </span>
            ) : (
              'Jetzt senden'
            )}
          </button>

          {reportCache.size > 0 && (
            <button
              onClick={openCacheModal}
              className="inline-flex items-center px-4 py-3 border border-borderBlue shadow-md text-sm font-semibold rounded-lg text-text bg-white hover:bg-lightgrey focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-borderBlue transition-all"
              title="Gespeicherte Berichte anzeigen und verwalten"
            >
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
              </svg>
              Cache verwalten ({reportCache.size})
            </button>
          )}
        </div>

        {/* Cache Explanation */}
        <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded">
          <h3 className="text-sm font-semibold text-blue-900 mb-2">ℹ️ Was bedeutet Cache?</h3>
          <p className="text-sm text-blue-800 mb-2">
            Der Cache speichert generierte Berichte temporär, um die Ladezeiten zu verkürzen und die Serverlast zu reduzieren.
          </p>
          <p className="text-sm text-blue-800 mb-2">
            <strong>Wie funktioniert es?</strong>
          </p>
          <ul className="text-sm text-blue-800 list-disc list-inside space-y-1 mb-2">
            <li>Berichte werden für 10 Minuten im Browser gespeichert (Frontend)</li>
            <li>Backend-Cache speichert Daten für 30 Minuten auf dem Server</li>
            <li>Wenn Sie denselben Bericht erneut aufrufen, wird er sofort aus dem Cache geladen</li>
            <li>Nach Ablauf der Gültigkeitsdauer (TTL) werden die Daten automatisch neu geladen</li>
          </ul>
          <p className="text-sm text-blue-800">
            <strong>Hinweis:</strong> Verwenden Sie "Cache verwalten", um gespeicherte Berichte anzusehen oder manuell zu löschen,
            wenn Sie aktuelle Daten benötigen.
          </p>
        </div>

        {/* Cache Status Info */}
        {reportCache.size > 0 && (
          <div className="mb-4 p-3 bg-silver border border-borderBlue rounded text-sm text-text">
            <span className="font-semibold">Cache-Status:</span> {reportCache.size} Bericht(e) gespeichert (TTL: 10 Minuten)
          </div>
        )}

        <div className="p-4 bg-silver border border-borderBlue rounded">
          <p className="text-sm mb-2 text-text">
            <strong>Geplante Zeit:</strong> Jeden {getDayName(schedule.dayOfWeek)} um {schedule.time} (Europa/Berlin Zeitzone)
          </p>
          <p className="text-sm text-grey">
            Der Wochenbericht wird automatisch an alle konfigurierten Empfänger gemäß dem Zeitplan gesendet.
            Sie können den Bericht auch jederzeit manuell für ein spezifisches Modul versenden.
          </p>
          <p className="text-sm text-grey mt-2">
            <strong>Hinweis:</strong> Die Empfänger werden nach Typ gruppiert (An/CC/BCC) beim Versenden der E-Mail.
          </p>
        </div>
      </section>

      {/* Preview Modal */}
      {isPreviewOpen && (
        <div className="fixed inset-0 bg-modalBackDropColor flex items-center justify-center z-[60]">
          <div className="bg-white rounded-lg p-6 w-[60vw] max-w-[1800px] max-h-[90vh] overflow-auto">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-left text-xl font-bold mb-4 border-none">Berichtsvorschau</h2>
              <div className="flex gap-2">
                <button
                  onClick={downloadBreakdownExcel}
                  disabled={downloadingExcel}
                  className={`inline-flex items-center px-4 py-2 border rounded-lg text-sm font-semibold transition-all ${
                    downloadingExcel
                      ? 'border-mainBlue bg-mainBlue text-white cursor-not-allowed'
                      : 'border-mainBlue text-mainBlue hover:bg-mainBlue hover:text-white'
                  }`}
                  title="Breakdown als Excel herunterladen"
                >
                  {downloadingExcel ? (
                    <>
                      <svg className="animate-spin h-4 w-4 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Wird generiert...
                    </>
                  ) : (
                    <>
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      Excel Download
                    </>
                  )}
                </button>
                <button
                  onClick={() => setIsPreviewOpen(false)}
                  className="text-grey hover:text-text"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            <div className="border border-borderBlue rounded p-4">
              <div
                dangerouslySetInnerHTML={{ __html: previewHTML }}
                onClick={(e) => {
                  // Handle "Mehr" link clicks for toggling breakdown sections
                  if (e.target.classList.contains('mehr-link')) {
                    const targetId = e.target.getAttribute('data-target');
                    if (targetId) {
                      const element = document.getElementById(targetId);
                      if (element) {
                        element.classList.toggle('visible');
                      }
                    }
                  }
                }}
              />
            </div>

            <button
              onClick={() => setIsPreviewOpen(false)}
              className="mt-4 inline-flex items-center px-6 py-3 border border-transparent shadow-md text-sm font-semibold rounded-lg text-white bg-grey hover:bg-text focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-grey transition-all"
            >
              Schließen
            </button>
          </div>
        </div>
      )}

      {/* Cache Management Modal */}
      <Modal visible={showCacheModal} onClose={() => setShowCacheModal(false)}>
        <div className="max-w-4xl">
          <h2 className="text-xl font-bold mb-4 text-text text-left pr-8">Cache Verwaltung</h2>

          <p className="text-sm text-grey mb-4 text-left">
            Hier sehen Sie alle gespeicherten Berichte mit ihren Filtereinstellungen.
            Sie können einzelne Berichte oder alle auf einmal löschen.
          </p>

          {reportCache.size === 0 ? (
            <div className="text-center py-8 text-grey">
              Keine Berichte im Cache
            </div>
          ) : (
            <>
              <div className="mb-4 text-sm text-grey text-left">
                <strong>Gesamt:</strong> {reportCache.size} Bericht(e) |
                <strong className="ml-2">TTL:</strong> 10 Minuten
              </div>

              <div className="max-h-96 overflow-y-auto mb-4">
                {getCacheEntries().map((entry, index) => (
                  <div
                    key={entry.key}
                    className="border border-borderBlue rounded-lg p-4 mb-3 bg-offWhite hover:bg-lightgrey transition-colors"
                  >
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        {/* Report Number */}
                        <div className="flex items-center gap-2 mb-2">
                          <span className="inline-flex items-center px-2 py-1 bg-mainBlue text-white text-xs font-semibold rounded">
                            Bericht #{index + 1}
                          </span>
                          <span className="text-xs text-grey">
                            Erstellt vor {entry.age} Minute{entry.age !== 1 ? 'n' : ''}
                          </span>
                        </div>

                        {/* Timestamp */}
                        <div className="text-sm text-text mb-3">
                          <strong>Erstellt am:</strong> {new Date(entry.timestamp).toLocaleString('de-DE', {
                            day: '2-digit',
                            month: '2-digit',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit',
                            second: '2-digit'
                          })}
                        </div>

                        {/* Filters */}
                        {entry.hasFilters ? (
                          <div className="space-y-2">
                            {entry.companies.length > 0 && (
                              <div>
                                <span className="text-sm font-semibold text-text">Firmenprojekte ({entry.companies.length}):</span>
                                <div className="flex flex-wrap gap-1 mt-1">
                                  {entry.companies.map((company, idx) => (
                                    <span key={idx} className="inline-block px-2 py-1 bg-white border border-borderBlue text-text text-xs rounded">
                                      {company}
                                    </span>
                                  ))}
                                </div>
                              </div>
                            )}

                            {entry.vps.length > 0 && (
                              <div>
                                <span className="text-sm font-semibold text-text">Vertriebspartner ({entry.vps.length}):</span>
                                <div className="flex flex-wrap gap-1 mt-1">
                                  {entry.vps.map((vp, idx) => (
                                    <span key={idx} className="inline-block px-2 py-1 bg-white border border-borderBlue text-text text-xs rounded">
                                      {vp}
                                    </span>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        ) : (
                          <div className="text-sm text-grey italic">
                            Keine Filter aktiv - Alle FPs/VPs enthalten
                          </div>
                        )}
                      </div>

                      {/* Delete Button */}
                      <button
                        onClick={() => invalidateSpecificCache(entry.key)}
                        className="ml-4 inline-flex items-center px-3 py-2 border border-cancel text-cancel hover:bg-cancel hover:text-white rounded-lg text-sm font-semibold transition-all"
                        title="Diesen Bericht aus dem Cache löschen"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              {/* Actions */}
              <div className="flex gap-3 pt-4 border-t border-borderBlue">
                <button
                  onClick={invalidateCache}
                  className="flex-1 inline-flex items-center justify-center px-4 py-3 border border-transparent shadow-md text-sm font-semibold rounded-lg text-white bg-cancel hover:bg-red focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-cancel transition-all"
                >
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                  Alle löschen
                </button>

                <button
                  onClick={() => setShowCacheModal(false)}
                  className="flex-1 inline-flex items-center justify-center px-4 py-3 border border-borderBlue shadow-md text-sm font-semibold rounded-lg text-text bg-white hover:bg-lightgrey focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-borderBlue transition-all"
                >
                  Schließen
                </button>
              </div>
            </>
          )}
        </div>
      </Modal>
      </div>
    </div>
  );
}
